import type { CreateRoleInput } from 'app/generated/gql/graphql'
import { useState } from 'react'
import { roleValidator } from 'app/components/roles/roleFormUtils'
import type { RoleJson, Permission } from 'app/types/entities'
import { useRoleStore } from 'app/stores/StoreContext'
import { closeModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { debug, logError } from 'lib/logging'

type RoleFormActionsProps = {
  roleId?: string
  isNewRole: boolean
  onSuccess?: () => void
}

type SubmitData = {
  name: string
  permissions?: string[] | string
}

type UseRoleFormActionsReturn = {
  isDuplicate: boolean
  formInFlight: boolean
  deleteInFlight: boolean
  handleDelete: () => Promise<void>
  handleSubmit: (data: SubmitData | undefined, allPermissions: Permission[]) => Promise<void>
}

type BuildRoleInputParams = {
  name: string
  permissions: Permission[]
}

type IsNoOpUpdateParams = {
  newName: string
  newPermissions: string[]
  originalName?: string
  originalPermissions?: string[]
}

export function buildRoleInput({ name, permissions }: BuildRoleInputParams): CreateRoleInput {
  return { name, permissions }
}

export function hasChanges({
  newName,
  newPermissions,
  originalName,
  originalPermissions,
}: IsNoOpUpdateParams): boolean {
  if (originalName === undefined || originalPermissions === undefined) return true
  if (newName !== originalName) return true
  if (newPermissions.length !== originalPermissions.length) return true
  const sorted = [...newPermissions].sort()
  const originalSorted = [...originalPermissions].sort()
  return !sorted.every((perm, idx) => perm === originalSorted[idx])
}

export function useRoleFormActions({ roleId, isNewRole, onSuccess }: RoleFormActionsProps): UseRoleFormActionsReturn {
  const store = useRoleStore()
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [formInFlight, setFormInFlight] = useState(false)
  const [deleteInFlight, setDeleteInFlight] = useState(false)

  async function handleDelete(): Promise<void> {
    if (!roleId) return
    setDeleteInFlight(true)
    const result = await store.remove(roleId)
    setDeleteInFlight(false)

    if (result.error) {
      debug(`roles`, `Error deleting role:`, result.error.message)
      notifyError(result.error.message)
    } else {
      closeModal()
    }
  }

  async function handleSubmit(data: SubmitData | undefined, allPermissions: Permission[]): Promise<void> {
    if (!data) return
    setIsDuplicate(false)
    debug(`roles`, `Form submission data:`, { data, allPermissions })

    const result = await roleValidator.validate(data)
    if (result.error) {
      logError(`error saving role`, result.error, result)
      notifyError(`Error Saving`)
      return
    }

    setFormInFlight(true)
    const input = buildRoleInput({ name: data.name, permissions: allPermissions })
    const opResult = isNewRole ? await store.create(input) : await store.update(roleId!, input)
    setFormInFlight(false)

    if (opResult.error) {
      debug(`roles`, `Error submitting role:`, opResult.error.message)
      if (opResult.error.message.includes(`already exists`)) {
        setIsDuplicate(true)
      } else {
        notifyError(opResult.error.message)
      }
    } else {
      closeModal()
      onSuccess?.()
    }
  }

  return {
    isDuplicate,
    formInFlight,
    deleteInFlight,
    handleDelete,
    handleSubmit,
  }
}
